import type { BlockModel } from './block-model.js';

type PropsInDraft =
  | 'version'
  | 'flavour'
  | 'role'
  | 'id'
  | 'keys'
  | 'text'
  | 'store';

type ModelProps<Model> = Model extends BlockModel<infer U> ? U : never;

const draftModelSymbol = Symbol('draftModel');

export type DraftModel<Model extends BlockModel = BlockModel> = Pick<
  Model,
  PropsInDraft
> & {
  children: DraftModel[];
  props: ModelProps<Model>;
  [draftModelSymbol]: true;
};

/** Native draft traversal; callers choose synchronous or cooperative consumption. */
export function* toDraftModelWork<Model extends BlockModel = BlockModel>(
  origin: Model
): Generator<void, DraftModel<Model>> {
  function* createDraft(
    model: BlockModel
  ): Generator<
    void,
    { draft: DraftModel; children: BlockModel[]; index: number }
  > {
    yield;
    const { id, version, flavour, role, keys, text, children } = model;
    const props = {} as ModelProps<BlockModel>;
    for (const key of keys) {
      yield;
      const target = model.props;
      // Like computed properties in the original spread, this creates an own
      // data property even for __proto__, without copying previous properties.
      Object.defineProperty(props, key, {
        value: target[key as keyof typeof target],
        enumerable: true,
        configurable: true,
        writable: true,
      });
    }
    const draft = {
      id,
      version,
      flavour,
      role,
      keys,
      text,
      children: [],
      props,
      [draftModelSymbol]: true,
    } as Omit<DraftModel, 'store'> as DraftModel;
    // The store of the origin, for readers of data the block keeps in other
    // documents of its workspace. Not enumerable: a draft is copied as data.
    Object.defineProperty(draft, 'store', {
      value: model.store,
      enumerable: false,
    });
    return { draft, children, index: 0 };
  }

  const root = yield* createDraft(origin);
  const stack = [root];
  while (stack.length) {
    const frame = stack[stack.length - 1];
    if (frame.index >= frame.children.length) {
      stack.pop();
      continue;
    }
    yield;
    const child = yield* createDraft(frame.children[frame.index++]);
    frame.draft.children.push(child.draft);
    stack.push(child);
  }
  return root.draft as DraftModel<Model>;
}

export function toDraftModel<Model extends BlockModel = BlockModel>(
  origin: Model
): DraftModel<Model> {
  const work = toDraftModelWork(origin);
  let next = work.next();
  while (!next.done) next = work.next();
  return next.value;
}
